import {
    DateFieldReference,
    DatePeriodConfiguration,
    EntityTemplateField,
    FieldConfigurationData,
    FieldConfigurationValueType,
    FormulaOperator,
    FormulaToken,
    FormulaTokenKind,
} from "../../EntityTemplate.types.ts";
import { InputFieldTypeEnum } from "../../inputFields/InputFields.types.ts";
import { isNullOrWhitespace } from "@common/stringOperations";
import { calculateConfiguredPeriod, ComputedFieldEntityValues, findCustomFieldValue, parseDatePeriod } from "./datePeriod.utils.ts";

export interface AllowedNextTokens {
    field: boolean;
    operator: boolean;
    openBracket: boolean;
    closeBracket: boolean;
}

export enum FormulaError {
    IncompleteToken = 1,
    UnexpectedToken = 2,
    FieldTypeNotAllowed = 3,
    ExpectsOperand = 4,
    UnclosedBracket = 5,
}

export interface CalculatedFieldResult {
    // No value is not the same as a value of zero, so the field is left empty the way the backend leaves the overview column empty
    value: number | undefined;
    currencyCode: string | undefined;
    hasConflictingCurrencies: boolean;
    // The period fields that left the result empty although other operands have values, so the field can say why it is empty
    emptyPeriodFieldIds: Array<string>;
}

interface AmountValue {
    amount: number;
    currencyCode: string | undefined;
}

type FormulaExpression = { fieldId: string; fieldType: InputFieldTypeEnum } | { operator: FormulaOperator; left: FormulaExpression; right: FormulaExpression };

export const formulaFieldTypes = [InputFieldTypeEnum.AmountField, InputFieldTypeEnum.NumberField, InputFieldTypeEnum.DatePeriodField];

// Mirrors CalculatedFieldFormula.LargestResult: the overview stores the result as decimal(28,2), so anything larger is left empty
const largestResult = 1e26;

export const formulaOperators = [FormulaOperator.Add, FormulaOperator.Subtract, FormulaOperator.Multiply, FormulaOperator.Divide];

export const operatorSymbols: Record<FormulaOperator, string> = {
    [FormulaOperator.Add]: "+",
    [FormulaOperator.Subtract]: "−",
    [FormulaOperator.Multiply]: "×",
    [FormulaOperator.Divide]: "÷",
};

// The example line shows what a formula does with round numbers, using the same value for every field of a type
const sampleOperandValues: Partial<Record<InputFieldTypeEnum, number>> = {
    [InputFieldTypeEnum.AmountField]: 1000,
    [InputFieldTypeEnum.NumberField]: 3,
    [InputFieldTypeEnum.DatePeriodField]: 3,
};

export function fieldToken(fieldId: string, fieldType: InputFieldTypeEnum): FormulaToken {
    return { kind: FormulaTokenKind.Field, fieldId, fieldType };
}

export function operatorToken(operator: FormulaOperator): FormulaToken {
    return { kind: FormulaTokenKind.Operator, operator };
}

export function bracketToken(open: boolean): FormulaToken {
    return { kind: FormulaTokenKind.Bracket, open };
}

export function parseFormula(configurationData: FieldConfigurationData | null | undefined): Array<FormulaToken> {
    if (configurationData?.type !== FieldConfigurationValueType.Json) {
        return [];
    }

    return (JSON.parse(configurationData.value) as { tokens?: Array<FormulaToken> }).tokens ?? [];
}

export function serializeFormula(tokens: Array<FormulaToken>): FieldConfigurationData {
    return { type: FieldConfigurationValueType.Json, value: JSON.stringify({ tokens }) };
}

// Without the formula flag a calculated field still only adds up amount fields, stored as A + B + C
export function sumTokens(fields: Array<{ id: string; type: InputFieldTypeEnum }>): Array<FormulaToken> {
    return fields.flatMap((field, index) => {
        const token = fieldToken(field.id, field.type);
        return index === 0 ? [token] : [operatorToken(FormulaOperator.Add), token];
    });
}

export function getFieldIds(tokens: Array<FormulaToken>): Array<string> {
    return [...new Set(tokens.filter((token) => token.kind === FormulaTokenKind.Field).map((token) => token.fieldId!))];
}

// Mirrors EntityTemplate.GetIdsOfFieldsReading in the backend, which refuses to delete a field while this returns anything
export function getNamesOfFieldsReading(templateFields: Array<EntityTemplateField>, readField: EntityTemplateField): Array<string> {
    return templateFields
        .filter((field) => reads(field, readField))
        .map((field) => field.customInputFieldName!)
        .sort();
}

// Mirrors NextTokenRules in the backend, judged by what comes before the caret. With the caret at the end the editor can only build
// incomplete formulas; an insert in the middle may leave the tokens after the caret misplaced until the user finishes, see findMisplacedTokenIndexes
export function allowedNext(tokens: Array<FormulaToken>, caret: number): AllowedNextTokens {
    return allowedAtEachPosition(tokens)[caret];
}

// The tokens findFormulaError would reject as unexpected, so the editor can mark them while the formula is being repaired
export function findMisplacedTokenIndexes(tokens: Array<FormulaToken>): Array<number> {
    const allowedAtPosition = allowedAtEachPosition(tokens);
    return tokens.flatMap((token, index) => (allows(allowedAtPosition[index], token) ? [] : [index]));
}

// What may stand at each of the tokens.length + 1 positions. A stray closing bracket does not count against later ones, or a bracket opened after it could never be closed
function allowedAtEachPosition(tokens: Array<FormulaToken>): Array<AllowedNextTokens> {
    let unclosedBrackets = 0;
    const allowedAfterEachToken = tokens.map((token, index) => {
        unclosedBrackets = Math.max(0, unclosedBrackets + bracketDepthChange(token));
        return allowedAfter(tokens[index], unclosedBrackets);
    });

    return [allowedAfter(undefined, 0), ...allowedAfterEachToken];
}

export function insertAt(tokens: Array<FormulaToken>, caret: number, token: FormulaToken): Array<FormulaToken> {
    return [...tokens.slice(0, caret), token, ...tokens.slice(caret)];
}

export function removeTokens(tokens: Array<FormulaToken>, indexes: Array<number>): Array<FormulaToken> {
    return tokens.filter((_, index) => !indexes.includes(index));
}

// Removals keep the surviving token objects, so the caret lands where the tokens that preceded it still are
export function caretAfterRemoval(tokensBefore: Array<FormulaToken>, tokensAfter: Array<FormulaToken>, caret: number): number {
    return tokensBefore.slice(0, caret).filter((token) => tokensAfter.includes(token)).length;
}

export function findMatchingBracket(tokens: Array<FormulaToken>, index: number): number | undefined {
    const token = tokens[index];
    if (token?.kind !== FormulaTokenKind.Bracket) {
        return undefined;
    }

    const step = token.open ? 1 : -1;
    let depth = 0;
    for (let position = index; position >= 0 && position < tokens.length; position += step) {
        depth += bracketDepthChange(tokens[position]) * step;
        if (depth === 0) {
            return position;
        }
    }

    return undefined;
}

// Mirrors CalculatedFieldFormula.FindError in the backend
export function findFormulaError(tokens: Array<FormulaToken>): FormulaError | undefined {
    let previous: FormulaToken | undefined = undefined;
    let unclosedBrackets = 0;
    for (const token of tokens) {
        if (isIncomplete(token)) {
            return FormulaError.IncompleteToken;
        }

        if (!allows(allowedAfter(previous, unclosedBrackets), token)) {
            return FormulaError.UnexpectedToken;
        }

        if (token.kind === FormulaTokenKind.Field && !formulaFieldTypes.includes(token.fieldType!)) {
            return FormulaError.FieldTypeNotAllowed;
        }

        unclosedBrackets += bracketDepthChange(token);
        previous = token;
    }

    if (previous !== undefined && expectsOperandAfter(previous)) {
        return FormulaError.ExpectsOperand;
    }

    return unclosedBrackets > 0 ? FormulaError.UnclosedBracket : undefined;
}

// Mirrors CalculatedFieldFormula.Evaluate in the backend: a blank operand counts as 0, but when no operand has a value there is nothing to calculate
export function evaluateFormula(tokens: Array<FormulaToken>, operandValue: (fieldId: string) => number | undefined): number | undefined {
    if (tokens.length === 0 || findFormulaError(tokens) !== undefined) {
        return undefined;
    }

    const values = new Map(getFieldIds(tokens).map((fieldId) => [fieldId, operandValue(fieldId)]));
    if ([...values.values()].every((value) => value === undefined) || hasEmptyPeriod(tokens, values)) {
        return undefined;
    }

    const result = evaluate(new ExpressionBuilder(tokens).build(), values);
    return result !== undefined && Math.abs(result) >= largestResult ? undefined : result;
}

export function calculateFormula(
    tokens: Array<FormulaToken>,
    entity: ComputedFieldEntityValues,
    periodConfigurations: Map<string, DatePeriodConfiguration>
): CalculatedFieldResult {
    const amountFieldIds = getFieldIds(tokens.filter((token) => token.fieldType === InputFieldTypeEnum.AmountField));
    const currencyCodes = getCurrencyCodesOfAmountsWithValue(amountFieldIds, entity);
    if (currencyCodes.length > 1) {
        return { value: undefined, currencyCode: undefined, hasConflictingCurrencies: true, emptyPeriodFieldIds: [] };
    }

    const fieldTypes = getFieldTypes(tokens);
    const operandValues = new Map(
        getFieldIds(tokens).map((fieldId) => [fieldId, readOperandValue(fieldId, fieldTypes.get(fieldId)!, entity, periodConfigurations)])
    );
    const value = evaluateFormula(tokens, (fieldId) => operandValues.get(fieldId));

    return {
        value,
        currencyCode: currencyCodes[0],
        hasConflictingCurrencies: false,
        emptyPeriodFieldIds: findEmptyPeriodFieldIds(fieldTypes, operandValues),
    };
}

// Nothing filled in at all is an entity not yet worked on rather than something to point out, so only periods missing among filled-in operands count
function findEmptyPeriodFieldIds(fieldTypes: Map<string, InputFieldTypeEnum>, operandValues: Map<string, number | undefined>): Array<string> {
    const emptyPeriodFieldIds = [...operandValues]
        .filter(([fieldId, value]) => fieldTypes.get(fieldId) === InputFieldTypeEnum.DatePeriodField && value === undefined)
        .map(([fieldId]) => fieldId);
    const hasFilledInOperand = [...operandValues.values()].some((value) => value !== undefined);

    return hasFilledInOperand ? emptyPeriodFieldIds : [];
}

export function describeFormula(tokens: Array<FormulaToken>, describeField: (token: FormulaToken) => string | undefined): string {
    return tokens
        .map((token) => describeToken(token, describeField))
        .join(" ")
        .replace(/\( /g, "(")
        .replace(/ \)/g, ")");
}

export function getSampleOperandValue(fieldType: InputFieldTypeEnum): number {
    return sampleOperandValues[fieldType] ?? 0;
}

export function evaluateSample(tokens: Array<FormulaToken>): number | undefined {
    const fieldTypes = getFieldTypes(tokens);
    return evaluateFormula(tokens, (fieldId) => getSampleOperandValue(fieldTypes.get(fieldId)!));
}

// An operator right before the caret is swapped rather than followed by another, since two operators in a row are never valid
export function addOperator(tokens: Array<FormulaToken>, operator: FormulaOperator, caret: number): Array<FormulaToken> {
    return hasOperatorBefore(tokens, caret) ? replaceToken(tokens, caret - 1, operatorToken(operator)) : insertAt(tokens, caret, operatorToken(operator));
}

export function hasOperatorBefore(tokens: Array<FormulaToken>, caret: number): boolean {
    return tokens[caret - 1]?.kind === FormulaTokenKind.Operator;
}

// Swapping a field for a field or an operator for an operator never breaks the formula, whereas swapping a bracket would
export function isReplaceable(token: FormulaToken): boolean {
    return token.kind !== FormulaTokenKind.Bracket;
}

export function replaceToken(tokens: Array<FormulaToken>, index: number, replacement: FormulaToken): Array<FormulaToken> {
    return tokens.map((token, tokenIndex) => (tokenIndex === index ? replacement : token));
}

// Removing a field also removes the operator that joined it to the formula, and a bracket pair left empty by that, so what remains still reads as a formula
export function removeField(tokens: Array<FormulaToken>, index: number): Array<FormulaToken> {
    return removeEmptyBrackets(removeOperand(tokens, index, index));
}

// An emptied bracket pair was an operand like a field, so it goes together with the operator that joined it, or two operators would be left side by side
function removeEmptyBrackets(tokens: Array<FormulaToken>): Array<FormulaToken> {
    const emptyPairStart = tokens.findIndex((token, index) => isOpenBracket(token) && isCloseBracket(tokens[index + 1]));
    if (emptyPairStart === -1) {
        return tokens;
    }

    return removeEmptyBrackets(removeOperand(tokens, emptyPairStart, emptyPairStart + 1));
}

function removeOperand(tokens: Array<FormulaToken>, operandStart: number, operandEnd: number): Array<FormulaToken> {
    const previous = tokens[operandStart - 1];
    const next = tokens[operandEnd + 1];
    const removeFrom = previous?.kind === FormulaTokenKind.Operator ? operandStart - 1 : operandStart;
    const removeTo = previous?.kind !== FormulaTokenKind.Operator && next?.kind === FormulaTokenKind.Operator ? operandEnd + 1 : operandEnd;

    return [...tokens.slice(0, removeFrom), ...tokens.slice(removeTo + 1)];
}

export function describeToken(token: FormulaToken, describeField: (token: FormulaToken) => string | undefined): string {
    switch (token.kind) {
        case FormulaTokenKind.Field:
            return describeField(token) ?? "";
        case FormulaTokenKind.Operator:
            return operatorSymbols[token.operator!];
        case FormulaTokenKind.Bracket:
            return token.open ? "(" : ")";
    }
}

function allowedAfter(previous: FormulaToken | undefined, unclosedBrackets: number): AllowedNextTokens {
    if (expectsOperandAfter(previous)) {
        return { field: true, operator: false, openBracket: true, closeBracket: false };
    }

    return { field: false, operator: true, openBracket: false, closeBracket: unclosedBrackets > 0 };
}

function expectsOperandAfter(previous: FormulaToken | undefined): boolean {
    return previous === undefined || previous.kind === FormulaTokenKind.Operator || isOpenBracket(previous);
}

function allows(allowedNextTokens: AllowedNextTokens, token: FormulaToken): boolean {
    switch (token.kind) {
        case FormulaTokenKind.Field:
            return allowedNextTokens.field;
        case FormulaTokenKind.Operator:
            return allowedNextTokens.operator;
        case FormulaTokenKind.Bracket:
            return token.open ? allowedNextTokens.openBracket : allowedNextTokens.closeBracket;
        default:
            return false;
    }
}

function isIncomplete(token: FormulaToken): boolean {
    switch (token.kind) {
        case FormulaTokenKind.Field:
            return isNullOrWhitespace(token.fieldId) || token.fieldType === undefined || token.fieldType === null;
        case FormulaTokenKind.Operator:
            return !Object.values(FormulaOperator).includes(token.operator!);
        case FormulaTokenKind.Bracket:
            return token.open === undefined || token.open === null;
        default:
            return true;
    }
}

function bracketDepthChange(token: FormulaToken): number {
    return isOpenBracket(token) ? 1 : isCloseBracket(token) ? -1 : 0;
}

function getFieldTypes(tokens: Array<FormulaToken>): Map<string, InputFieldTypeEnum> {
    return new Map(tokens.filter((token) => token.kind === FormulaTokenKind.Field).map((token) => [token.fieldId!, token.fieldType!]));
}

function reads(field: EntityTemplateField, readField: EntityTemplateField): boolean {
    switch (field.customInputFieldType) {
        case InputFieldTypeEnum.CalculatedField:
            return !!readField.customInputFieldId && getFieldIds(parseFormula(field.configurationData)).includes(readField.customInputFieldId);
        case InputFieldTypeEnum.DatePeriodField: {
            const period = parseDatePeriod(field.configurationData);
            return period !== undefined && (isDateField(period.from, readField) || isDateField(period.to, readField));
        }
        default:
            return false;
    }
}

function isDateField(reference: DateFieldReference, field: EntityTemplateField): boolean {
    return reference.customFieldId
        ? reference.customFieldId === field.customInputFieldId
        : !!reference.standardField && reference.standardField === field.standardFieldIdentifier;
}

function isOpenBracket(token: FormulaToken | undefined): boolean {
    return token?.kind === FormulaTokenKind.Bracket && token.open === true;
}

function isCloseBracket(token: FormulaToken | undefined): boolean {
    return token?.kind === FormulaTokenKind.Bracket && token.open === false;
}

// An empty period means a date is missing or the end lies before the start. Counting it as 0 would show a contract without an
// end date as worth 0 rather than as not calculable, so it empties the whole formula instead.
function hasEmptyPeriod(tokens: Array<FormulaToken>, values: Map<string, number | undefined>): boolean {
    return tokens.some((token) => token.fieldType === InputFieldTypeEnum.DatePeriodField && values.get(token.fieldId!) === undefined);
}

function evaluate(expression: FormulaExpression, values: Map<string, number | undefined>): number | undefined {
    if ("fieldId" in expression) {
        return values.get(expression.fieldId) ?? 0;
    }

    return apply(expression.operator, evaluate(expression.left, values), evaluate(expression.right, values));
}

// Division by zero gives no value, which then empties the whole result, the same as NULLIF does in the generated SQL
function apply(operator: FormulaOperator, left: number | undefined, right: number | undefined): number | undefined {
    if (left === undefined || right === undefined) {
        return undefined;
    }

    switch (operator) {
        case FormulaOperator.Add:
            return left + right;
        case FormulaOperator.Subtract:
            return left - right;
        case FormulaOperator.Multiply:
            return left * right;
        case FormulaOperator.Divide:
            return right === 0 ? undefined : left / right;
    }
}

function readOperandValue(
    fieldId: string,
    fieldType: InputFieldTypeEnum,
    entity: ComputedFieldEntityValues,
    periodConfigurations: Map<string, DatePeriodConfiguration>
): number | undefined {
    if (fieldType === InputFieldTypeEnum.DatePeriodField) {
        return calculateConfiguredPeriod(periodConfigurations.get(fieldId), entity);
    }

    const value = findCustomFieldValue(fieldId, entity);
    return fieldType === InputFieldTypeEnum.AmountField ? readAmountValue(value)?.amount : readNumberValue(value);
}

// Only amounts that actually take part in the result can put the currencies in conflict. An amount field left blank still
// carries a currency code, so including those would warn about nothing.
function getCurrencyCodesOfAmountsWithValue(amountFieldIds: Array<string>, entity: ComputedFieldEntityValues): Array<string> {
    const currencyCodes = amountFieldIds
        .map((fieldId) => readAmountValue(findCustomFieldValue(fieldId, entity))?.currencyCode)
        .filter((currencyCode): currencyCode is string => !isNullOrWhitespace(currencyCode));

    return [...new Set(currencyCodes)];
}

function readAmountValue(value: any): AmountValue | undefined {
    // Amount values are read back from the backend pascal cased, but written from the client camel cased
    const amount = value?.Amount ?? value?.amount;
    if (typeof amount !== "number") {
        return undefined;
    }

    return { amount: amount, currencyCode: value?.CurrencyCode ?? value?.currencyCode };
}

function readNumberValue(value: any): number | undefined {
    if (value === null || value === undefined || isNullOrWhitespace(String(value))) {
        return undefined;
    }

    const number = Number(value);
    return isNaN(number) ? undefined : number;
}

// Recursive descent over tokens already validated by findFormulaError: multiply and divide bind tighter than add and subtract, all left-associative
class ExpressionBuilder {
    private position = 0;

    constructor(private readonly tokens: Array<FormulaToken>) {}

    build(): FormulaExpression {
        return this.buildSum();
    }

    private buildSum(): FormulaExpression {
        let left = this.buildProduct();
        while (this.nextIsOperator(FormulaOperator.Add, FormulaOperator.Subtract)) {
            const operator = this.takeNext().operator!;
            left = { operator, left, right: this.buildProduct() };
        }

        return left;
    }

    private buildProduct(): FormulaExpression {
        let left = this.buildOperand();
        while (this.nextIsOperator(FormulaOperator.Multiply, FormulaOperator.Divide)) {
            const operator = this.takeNext().operator!;
            left = { operator, left, right: this.buildOperand() };
        }

        return left;
    }

    private buildOperand(): FormulaExpression {
        const token = this.takeNext();
        if (isOpenBracket(token)) {
            const bracketed = this.buildSum();
            this.takeNext();
            return bracketed;
        }

        return { fieldId: token.fieldId!, fieldType: token.fieldType! };
    }

    private nextIsOperator(...operators: Array<FormulaOperator>): boolean {
        const next = this.tokens[this.position];
        return next?.kind === FormulaTokenKind.Operator && operators.includes(next.operator!);
    }

    private takeNext(): FormulaToken {
        return this.tokens[this.position++];
    }
}
